// SearchFilter.tsx - Search and filter component for scenarios

import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ScenarioFilterOptions } from '../../types/ScenarioManagement';

interface SearchFilterProps {
  onFilterChange: (filters: ScenarioFilterOptions) => void;
  viewMode: 'grid' | 'table';
  onViewModeChange: (mode: 'grid' | 'table') => void;
  showDeleted: boolean;
  onShowDeletedChange: (show: boolean) => void;
}

const SearchFilter: React.FC<SearchFilterProps> = ({
  onFilterChange,
  viewMode,
  onViewModeChange,
  showDeleted,
  onShowDeletedChange
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState<ScenarioFilterOptions>({});

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      onFilterChange({ ...filters, search: searchTerm });
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm, filters, onFilterChange]);

  const handleFilterUpdate = (key: keyof ScenarioFilterOptions, value: any) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    onFilterChange(newFilters);
  };

  const clearFilters = () => {
    setSearchTerm('');
    setFilters({});
    onFilterChange({});
  };

  const activeFilterCount = Object.keys(filters).filter(key => 
    filters[key as keyof ScenarioFilterOptions] !== undefined && 
    key !== 'search'
  ).length + (searchTerm ? 1 : 0);

  return (
    <div className="mb-6 space-y-4">
      {/* Search Bar */}
      <div className="flex gap-4">
        <div className="flex-1 relative">
          <svg 
            className="absolute left-4 top-1/2 transform -translate-y-1/2 text-slate-400"
            width="20" 
            height="20" 
            viewBox="0 0 24 24" 
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <circle cx="11" cy="11" r="8" />
            <path d="m21 21-4.35-4.35" />
          </svg>
          <input
            type="text"
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            placeholder="Search scenarios by title, description, or tags..."
            className="w-full pl-12 pr-4 py-3 bg-white/[0.05] border border-white/10 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-professional-blue-500 transition-colors"
          />
          {searchTerm && (
            <button
              onClick={() => setSearchTerm('')}
              className="absolute right-4 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-white"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
              </svg>
            </button>
          )}
        </div>

        {/* Filter Toggle */}
        <button
          onClick={() => setShowFilters(!showFilters)}
          className={`px-4 py-3 rounded-lg border transition-all flex items-center gap-2 ${
            showFilters || activeFilterCount > 0
              ? 'bg-professional-blue-600/20 border-professional-blue-500 text-professional-blue-400'
              : 'bg-white/[0.05] border-white/10 text-slate-400 hover:text-white hover:border-white/20'
          }`}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
            <path d="M10 18h4v-2h-4v2zM3 6v2h18V6H3zm3 7h12v-2H6v2z"/>
          </svg>
          Filters
          {activeFilterCount > 0 && (
            <span className="ml-1 px-2 py-0.5 bg-professional-blue-600 text-white text-xs rounded-full">
              {activeFilterCount}
            </span>
          )}
        </button>

        {/* View Mode Toggle */}
        <div className="flex bg-white/[0.05] rounded-lg p-1">
          <button
            onClick={() => onViewModeChange('grid')}
            className={`px-3 py-2 rounded transition-all ${
              viewMode === 'grid'
                ? 'bg-professional-blue-600 text-white'
                : 'text-slate-400 hover:text-white'
            }`}
            title="Grid View"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <path d="M4 8h4V4H4v4zm6 12h4v-4h-4v4zm-6 0h4v-4H4v4zm0-6h4v-4H4v4zm6 0h4v-4h-4v4zm6-10v4h4V4h-4zm-6 4h4V4h-4v4zm6 6h4v-4h-4v4zm0 6h4v-4h-4v4z"/>
            </svg>
          </button>
          <button
            onClick={() => onViewModeChange('table')}
            className={`px-3 py-2 rounded transition-all ${
              viewMode === 'table'
                ? 'bg-professional-blue-600 text-white'
                : 'text-slate-400 hover:text-white'
            }`}
            title="Table View"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <path d="M3 3h18v2H3V3zm0 4h18v2H3V7zm0 4h18v2H3v-2zm0 4h18v2H3v-2zm0 4h18v2H3v-2z"/>
            </svg>
          </button>
        </div>
      </div>

      {/* Expanded Filters */}
      {showFilters && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
          className="bg-white/[0.03] border border-white/10 rounded-lg p-6 space-y-4"
        >
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Difficulty Filter */}
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Difficulty
              </label>
              <div className="space-y-2">
                {['Beginner', 'Intermediate', 'Advanced', 'Expert', 'Tech', 'Exec'].map(level => (
                  <label key={level} className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={(filters.difficulty || []).includes(level as any)}
                      onChange={e => {
                        const current = filters.difficulty || [];
                        if (e.target.checked) {
                          handleFilterUpdate('difficulty', [...current, level]);
                        } else {
                          handleFilterUpdate('difficulty', current.filter(d => d !== level));
                        }
                      }}
                      className="mr-3 w-4 h-4 text-professional-blue-600 bg-white/[0.05] border-white/20 rounded focus:ring-professional-blue-500"
                    />
                    <span className="text-white">{level}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Category Filter */}
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Category
              </label>
              <div className="space-y-2">
                {['Geographic', 'Technology', 'Business', 'Crisis', 'Strategy'].map(cat => (
                  <label key={cat} className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={(filters.category || []).includes(cat as any)}
                      onChange={e => {
                        const current = filters.category || [];
                        if (e.target.checked) {
                          handleFilterUpdate('category', [...current, cat]);
                        } else {
                          handleFilterUpdate('category', current.filter(c => c !== cat));
                        }
                      }}
                      className="mr-3 w-4 h-4 text-professional-blue-600 bg-white/[0.05] border-white/20 rounded focus:ring-professional-blue-500"
                    />
                    <span className="text-white">{cat}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Availability Filter */}
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Availability
              </label>
              <div className="space-y-2">
                <label className="flex items-center cursor-pointer">
                  <input
                    type="radio"
                    name="availability"
                    checked={filters.isAvailable === undefined}
                    onChange={() => handleFilterUpdate('isAvailable', undefined)}
                    className="mr-3"
                  />
                  <span className="text-white">All</span>
                </label>
                <label className="flex items-center cursor-pointer">
                  <input
                    type="radio"
                    name="availability"
                    checked={filters.isAvailable === true}
                    onChange={() => handleFilterUpdate('isAvailable', true)}
                    className="mr-3"
                  />
                  <span className="text-white">Available</span>
                </label>
                <label className="flex items-center cursor-pointer">
                  <input
                    type="radio"
                    name="availability"
                    checked={filters.isAvailable === false}
                    onChange={() => handleFilterUpdate('isAvailable', false)}
                    className="mr-3"
                  />
                  <span className="text-white">Coming Soon</span>
                </label>
              </div>
            </div>
          </div>

          {/* Additional Options */}
          <div className="flex items-center justify-between pt-4 border-t border-white/10">
            <label className="flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={showDeleted}
                onChange={e => onShowDeletedChange(e.target.checked)}
                className="mr-3 w-4 h-4 text-professional-blue-600 bg-white/[0.05] border-white/20 rounded focus:ring-professional-blue-500"
              />
              <span className="text-slate-300">Show deleted scenarios</span>
            </label>

            <button
              onClick={clearFilters}
              className="text-professional-blue-400 hover:text-professional-blue-300 transition-colors"
            >
              Clear all filters
            </button>
          </div>
        </motion.div>
      )}
    </div>
  );
};

export default SearchFilter;